import React, { useEffect, useRef, useState } from 'react';
import { Card, Col, Collapse, Row } from 'react-bootstrap';
import { SortModule } from 'tabulator-tables';
import { Tabulator, PageModule, DownloadModule, ExportModule, ResizeColumnsModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule, ResizeColumnsModule]);

interface BatchSummary {
  entity_id: number;
  entity_name: string;
  batch_id: string;
  file_status: string;
  govt_lvl: string;
  min_posting_date: Date;
  max_posting_date: Date;
  record_count: number;
  net_amount: number;
  net_amount_rv: number;
  net_amount_rx: number;
  net_amount_py: number;
  fiscal_years: string;
  types: string;
  quarters: string;
  sf_id: string;
  upload_date: string;
  file_name: string;
}

interface EntityBatchListOverviewProps {
  batchSummaryData: BatchSummary[];
  parentEntityName?: string;
  parentEntityId?: number | null;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  loading?: boolean;
  activeTab?: string;
}

const EntityBatchListOverview: React.FC<EntityBatchListOverviewProps> = (
  { batchSummaryData, 
    parentEntityName, 
    parentEntityId,
    formatCurrency,
    formatEntityString,
    loading,
    activeTab,
  }) => {
    const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
    const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
      const formattedEntityName = parentEntityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
      if (!tabulatorRef.current) return;
      tabulatorRef.current.download(type, `${formattedEntityName}_batch_list.${type}`);
    };

    useEffect(() => {
      if (!tableRef.current) return;

      if (tabulatorRef.current) {
        try {
          tabulatorRef.current.setData(Array.isArray(batchSummaryData) ? batchSummaryData : [])
            .then(() => {
                if (tabulatorRef.current) {
                    tabulatorRef.current.setLayout(window.innerWidth < 1025 ? "fitDataFill" : "fitDataStretch");
                    tabulatorRef.current.redraw(true);
                }
            });
        } catch (err) {
          console.warn("setData failed:", err);
        }
        return;
      }

      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

      tabulatorRef.current = new Tabulator(tableRef.current, {
        minHeight: "450px",
        maxHeight: "100%",
        layout: mobile ? "fitDataFill" : "fitDataStretch",
        resizableColumnFit: false,
        resizableColumnGuide: true,
        columnHeaderVertAlign: "bottom",
        pagination: "local",
        paginationSize: 10,
        placeholder: "No Data Available",
        initialSort: [
          {
            column: "upload_date",
            dir: "desc",
          }
        ],
        columns: [ 
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-objects-column'></i> Batch Id";
              return el;
              },
              title: "Batch ID",
              field: "batch_id",
              resizable: true,
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-clipboard-list'></i> Summary";
              return el;
              },
              title: "Summary",
              field: "summary",
              resizable: true,
              headerSort: true,
              sorter: (a: any, b: any, aRow: any, bRow: any) => {
                const getSortVal = (row: any) => {
                  const data = row.getData();
                  const uploadDate = new Date(data.upload_date);
                  const isComp = data.types?.includes("PY") && !data.types?.includes("EX");
                  return (uploadDate >= new Date("2024-09-01") && !isComp) ? 1 : 0;
                };

                return getSortVal(aRow) - getSortVal(bRow);
              },
              formatter: (cell: any) => {
                const data = cell.getData();
                const dateStr = data.upload_date;

                if (!dateStr) return `<span class="btn btn-primary btn-sm">Unavailable</span>`;

                const threshold = new Date(2024, 8, 1).getTime();

                // Updated date parsing
                let uploadTime;
                if (dateStr.includes('-')) {
                    const [y, m, d] = dateStr.split('-').map(Number);
                    uploadTime = new Date(y, m - 1, d).getTime();
                } else if (dateStr.includes('/')) {
                    const [m, d, y] = dateStr.split('/').map(Number);
                    uploadTime = new Date(y, m - 1, d).getTime();
                } else {
                    uploadTime = new Date(dateStr).getTime();
                }

                if (uploadTime >= threshold) {
                    const batchId = data.batch_id;
                    const entityId = data.entity_id || "25";
                    const url = `https://old.transparent.utah.gov/batch_summary.php?entity_id=${entityId}&batch_id=${batchId}`;
                    
                    return `<a href="${url}" target="_blank" class="btn btn-success btn-sm" style="text-decoration: none !important; color: white;">Open</a>`;
                }

                return `<span class="btn btn-primary btn-sm" style="pointer-events: none;">Unavailable</span>`;
              }
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-list-check'></i> Status";
              return el;
              },
              title: "Status",
              field: "file_status",
              resizable: true,
              headerSort: true,
              formatter: (cell: any) => {
                if (cell.getValue() === "Rejected") {
                  return `<span class='btn btn-sm btn-danger'>Rejected</span>`;
                }
                else if (cell.getValue() === "Loaded") {
                  return `<span class='btn btn-sm' style="background-color: #4a5568; color: white">Loaded</span>`;
                }
                else if (cell.getValue() === "Deleted") {
                  return `<span class='btn btn-sm' style="background-color: #4a5568; color: white">Deleted</span>`;
                }
              }
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-calendar'></i> Upload Date";
              return el;
              },
              title: "Upload Date",
              field: "upload_date",
              resizable: true,
              headerSort: true,
              formatter: (cell: any) => {
                const value = cell.getValue();
                if (!value) return "";

                const parts = value.split('-');
                if (parts.length !== 3) return value;

                return `${parts[1]}/${parts[2]}/${parts[0]}`;
              }
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-folder'></i> File Name";
              return el;
              },
              title: "File Name",
              field: "file_name",
              resizable: true,
              headerSort: true,
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-dollar-sign'></i> Wages";
              return el;
              },
              title: "Net Amount",
              field: "net_amount",
              resizable: true,
              headerSort: true,
              sorter: "number",
              formatter: (cell: any) => formatCurrency(cell.getValue()),
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-folder'></i> Records";
              return el;
              },
              title: "Records",
              field: "record_count",
              headerSort: true,
              sorter: "number",
              formatter: "money",
              formatterParams: {
                thousand: ",",
                symbol: "",
                symbolAfter: "",
                negativeSign: true,
                precision: 0,
              }
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-calendar'></i> Min Date";
              return el;
              },
              title: "Min Date",
              field: "min_posting_date",
              resizable: true,
              headerSort: true,
              formatter: (cell: any) => {
                const value = cell.getValue();
                if (!value) return "";

                const parts = value.split('-');
                if (parts.length !== 3) return value;

                return `${parts[1]}/${parts[2]}/${parts[0]}`;
              }
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-calendar'></i> Max Date";
              return el;
              },
              title: "Max Date",
              field: "max_posting_date",
              resizable: true,
              headerSort: true,
              formatter: (cell: any) => {
                const value = cell.getValue();
                if (!value) return "";

                const parts = value.split('-');
                if (parts.length !== 3) return value;

                return `${parts[1]}/${parts[2]}/${parts[0]}`;
              }
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-calendar'></i> Fiscal Years";
              return el;
              },
              title: "Fiscal Years",
              field: "fiscal_years",
              resizable: true,
              headerSort: true,
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-layer-group'></i> Types";
              return el;
              },
              title: "Types",
              field: "types",
              resizable: true,
              headerSort: true,
            },
            {
              titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-calendar'></i> Quarters";
              return el;
              },
              title: "Quarters",
              field: "quarters",
              resizable: true,
              headerSort: true,
            }
        ],
        data: Array.isArray(batchSummaryData) ? batchSummaryData : [],
      });

      setTimeout(() => {
        if (tabulatorRef.current) tabulatorRef.current.redraw(true);
      }, 100);

      const handleResize = () => {
        if (!tabulatorRef.current) return;
        const mobileResize = window.innerWidth < 1025;
        tabulatorRef.current.setLayout(mobileResize ? "fitDataFill" : "fitDataStretch");
        tabulatorRef.current.redraw(true);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        if (tabulatorRef.current) {
            tabulatorRef.current.destroy();
            tabulatorRef.current = null;
        }
      };
    }, [loading, activeTab, batchSummaryData]);

  return (
      <Row className="mb-2">
        <Col xs={12}>
          <Card 
          bg="secondary" text="white" 
          style={{ border: "none", borderRadius: "0" }}>
            <Card.Body style={{ justifyItems: "center" }}>
                <div className="tu_tabHeader">
                  <div className="tu-tabHeader__titleRow">
                    <h1 className="tu-tabHeader__title">
                      Transparency Report</h1>
                      <button
                        type="button"
                        className="tu-tabHeader__infoBtn"
                        onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                        aria-label="Transparency Report info"
                      >
                        <i className="fas fa-info"/>
                      </button>
                  </div>
                </div>
                <Collapse in={showHeaderInfo}>
                  <div>
                    <p className="lead mt-2 text-center">
                      The transparency report tab provides a list of all the batches submitted by the entity. Results can be sorted by factors such as upload date, status, or fiscal year.
                    </p>
                    <p className="lead mt-2 text-center">
                      For uploads attempted after 9/1/2024, there will be a green “Open” button available in the Summary column. Clicking this button will take you to a detailed summary page for that upload.
                    </p>
                  </div>
                </Collapse>
            </Card.Body>
          </Card>

          {batchSummaryData.length > 0 ? 
          (
          <>
            <Card className="mb-4">
              <Row className="py-4 px-4">
                <Col>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div ref={tableRef} className="table-light" />
                  <div style={{ width: "100%" }}>
                    <div ref={tableRef} className="table-light" style={{ minHeight: 400 }} />
                  </div>
                </Col>
              </Row>
            </Card>
          </>
          ) : (
          <>
            <Card className="mb-4">
              <div className="p-5 text-center">
                <div className="alert alert-light shadow-sm">
                  No data available
                </div>
              </div>
            </Card>
          </>
          )}
          
        </Col>
      </Row>
  );
};

export default EntityBatchListOverview;